import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Search,
  Users,
  UserCog,
  Package,
  Layers,
  Warehouse,
  ArrowDownToLine,
  ArrowUpFromLine,
  ClipboardList,
  ArrowLeftRight,
  Receipt,
  ScanBarcode,
  Building2,
  SlidersHorizontal,
  MapPin,
  Plug,
  Truck,
  ClipboardCheck,
  Smartphone,
  PackageMinus,
  PackagePlus,
  PackageX,
  AlertTriangle,
  Send,
  Clock,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ShieldCheck,
  ScrollText,
  FlaskConical,
  Boxes,
  Tags,
  Factory,
  FileText,
  Route as RouteIcon,
  ShoppingCart,
  Inbox,
  Calculator,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { screenForPath } from '@wsfull/shared'
import { useAuth } from '../contexts/auth'
import { usePermissions } from '../contexts/permissions'
import { useTenant } from '../contexts/tenant'
import { api } from '../lib/api'
import { cn } from '../lib/cn'

type Tenant = { id: string; name: string; active: boolean }
type NavItem = { to: string; label: string; Icon: typeof Users; adminOnly?: boolean; agent?: boolean; client?: boolean }
type NavGroup = { label: string | null; items: NavItem[] }

const navGroups: NavGroup[] = [
  { label: null, items: [
    { to: '/', label: 'Dashboard', Icon: LayoutDashboard, agent: true, client: true },
  ] },
  { label: 'Operação', items: [
    { to: '/orders',      label: 'Pedidos',        Icon: ClipboardList, agent: true },
    { to: '/separacao-arquivo', label: 'Separação', Icon: Layers, agent: true },
    { to: '/despachar',   label: 'Despachar',      Icon: Truck, agent: true },
    { to: '/expedir',     label: 'Expedir (scan)', Icon: ScanBarcode, agent: true },
    { to: '/cockpit',     label: 'Cockpit',        Icon: ScanBarcode, agent: true },
    { to: '/meus-envios', label: 'Meus envios',    Icon: Send, client: true },
    { to: '/rastreio-lote', label: 'Rastreio em lote', Icon: Search, agent: true, client: true },
    { to: '/minha-producao', label: 'Minha produção', Icon: FlaskConical, client: true },
    { to: '/outbound',    label: 'Ordens manuais', Icon: ArrowUpFromLine },
  ] },
  { label: 'Estoque', items: [
    { to: '/stock',           label: 'Saldos',          Icon: Warehouse, agent: true, client: true },
    { to: '/abastecimento',   label: 'Abastecimento',   Icon: PackagePlus, agent: true },
    { to: '/posicao-estoque', label: 'Posição diária',  Icon: Clock, client: true },
    { to: '/conferencia-fracionamento', label: 'Conf. Fracionamento', Icon: ClipboardCheck, agent: true },
    { to: '/conferencia-armazem',       label: 'Conf. Armazém',       Icon: ClipboardCheck, agent: true },
    { to: '/packing-list',    label: 'Packing list',    Icon: ClipboardList, agent: true },
    { to: '/contagem-mobile', label: 'Contagem',        Icon: Smartphone, adminOnly: true },
    { to: '/divergencias',    label: 'Divergências',      Icon: AlertTriangle, agent: true },
    { to: '/inbound',         label: 'Recebimento',     Icon: ArrowDownToLine, agent: true },
    { to: '/transfers',       label: 'Transferências',  Icon: ArrowLeftRight },
    { to: '/adjustments',     label: 'Ajustes',         Icon: SlidersHorizontal },
    { to: '/movimentacoes',   label: 'Movimentações',   Icon: Clock },
    { to: '/defeito',         label: 'Defeito',         Icon: PackageX },
    { to: '/lots',            label: 'Lotes',           Icon: ScanBarcode },
  ] },
  { label: 'Armazém', items: [
    { to: '/pallets',    label: 'Paletes',      Icon: Layers, agent: true },
    { to: '/locations',  label: 'Localizações', Icon: MapPin,     adminOnly: true },
    { to: '/warehouses', label: 'Armazéns',     Icon: Building2,  adminOnly: true },
  ] },
  { label: 'Indústria', items: [
    { to: '/industria/materiais',   label: 'Materiais',   Icon: Boxes },
    { to: '/industria/recebimento', label: 'Recebimento', Icon: FlaskConical, agent: true },
    { to: '/industria/etiquetas',   label: 'Etiquetas',   Icon: Tags, agent: true },
    { to: '/industria/fichas',      label: 'Fichas técnicas', Icon: FileText },
    { to: '/industria/simulador',   label: 'Dá para produzir?', Icon: Calculator },
    { to: '/industria/ops',         label: 'Ordens de produção', Icon: Factory },
    { to: '/industria/apontamento', label: 'Apontamento', Icon: ScanBarcode, agent: true },
    { to: '/industria/rastreabilidade', label: 'Rastreabilidade', Icon: RouteIcon },
    { to: '/industria/compras',      label: 'Compras',       Icon: ShoppingCart },
    { to: '/industria/fornecedores', label: 'Fornecedores',  Icon: Truck },
    { to: '/industria/pagar',        label: 'Contas a pagar', Icon: Receipt, adminOnly: true },
    { to: '/industria/faturamento',  label: 'Faturamento',   Icon: Receipt, adminOnly: true },
    { to: '/industria/solicitacoes', label: 'Solicitações',  Icon: Inbox },
    { to: '/industria/embalagem',    label: 'Ficha de embalagem', Icon: PackagePlus },
  ] },
  { label: 'Cadastros', items: [
    { to: '/clients',  label: 'Clientes', Icon: Users },
    { to: '/products', label: 'Produtos', Icon: Package },
    { to: '/users',    label: 'Usuários', Icon: UserCog, adminOnly: true },
  ] },
  { label: 'Gestão', items: [
    { to: '/billing',      label: 'Faturamento', Icon: Receipt },
    { to: '/integrations', label: 'Integrações', Icon: Plug, adminOnly: true },
    { to: '/auditoria',    label: 'Logs', Icon: ScrollText, adminOnly: true },
    { to: '/permissions',  label: 'Controle de acessos', Icon: ShieldCheck, adminOnly: true },
  ] },
]

// Grupos recolhidos por padrão (os menos usados no dia a dia).
const DEFAULT_COLLAPSED = ['Armazém', 'Indústria', 'Cadastros', 'Gestão']

export function Layout() {
  const { user, logout } = useAuth()
  const { can } = usePermissions()
  const { activeTenantId, setActiveTenantId } = useTenant()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const isAdmin = user?.role === 'admin'
  const isClient = user?.role === 'client'
  // Pode escolher o cliente ativo: admin, ou operador/agente que não está preso a um cliente.
  const canSwitchTenant = isAdmin || (!isClient && !user?.tenantId)

  // Visibilidade do menu pelo controle de acessos: mostra a tela se o perfil tem ao menos leitura.
  const keyOf = (to: string) => screenForPath(to)?.key
  const groups = navGroups
    .map(g => ({ ...g, items: g.items.filter(i => { const k = keyOf(i.to); return !!k && can(k, 'read') }) }))
    .filter(g => g.items.length > 0)
  const allItems = groups.flatMap(g => g.items)

  // Guarda de rota: se cair numa tela sem acesso (ex.: URL digitada), bloqueia a área de conteúdo.
  const currentScreen = screenForPath(pathname)
  const blocked = !!currentScreen && !can(currentScreen.key, 'read')

  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('nav_collapsed')
      return new Set(saved ? JSON.parse(saved) : DEFAULT_COLLAPSED)
    } catch { return new Set(DEFAULT_COLLAPSED) }
  })
  function toggleGroup(label: string) {
    setCollapsed(s => {
      const n = new Set(s)
      n.has(label) ? n.delete(label) : n.add(label)
      try { localStorage.setItem('nav_collapsed', JSON.stringify([...n])) } catch { /* ignore */ }
      return n
    })
  }
  const itemClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all',
      isActive
        ? 'bg-blue-50 text-blue-700 font-semibold ring-1 ring-inset ring-blue-100 shadow-sm'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
    )

  const { data: clients = [] } = useQuery<Tenant[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data,
    enabled: canSwitchTenant,
  })

  const activeClient = clients.find(c => c.id === activeTenantId)

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {open && (
        <div className="fixed inset-0 z-20 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-gray-200 bg-white transition-transform lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Brand */}
        <div className="flex h-14 items-center border-b border-gray-100 px-5">
          <span className="font-display text-xl leading-none text-gradient">WsFull</span>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}>
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Seletor de cliente ativo (admin, ou operador/agente sem cliente fixo) */}
        {canSwitchTenant && (
          <div className="border-b border-gray-100 px-3 py-2">
            <p className="mb-1 px-1 text-xs font-medium text-gray-400 uppercase tracking-wide">Cliente ativo</p>
            <div className="relative">
              <select
                className="w-full appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={activeTenantId}
                onChange={e => setActiveTenantId(e.target.value)}
              >
                <option value="">Todos os clientes</option>
                {clients.filter(c => c.active).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            </div>
            {activeTenantId && (
              <button
                className="mt-1 w-full text-left px-1 text-xs text-gray-400 hover:text-gray-600"
                onClick={() => setActiveTenantId('')}
              >
                × Limpar seleção
              </button>
            )}
          </div>
        )}

        {/* Nav links (agrupados / colapsáveis) */}
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {groups.map((g, gi) => {
            const renderItems = (
              <ul className="space-y-0.5">
                {g.items.map(({ to, label, Icon }) => (
                  <li key={to}>
                    <NavLink to={to} end={to === '/'} onClick={() => setOpen(false)} className={itemClass}>
                      <Icon className="h-4 w-4 shrink-0" />
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )
            if (!g.label) return <div key={gi} className="mb-1">{renderItems}</div>
            const hasActive = g.items.some(i => i.to !== '/' && pathname.startsWith(i.to))
            const shown = hasActive || !collapsed.has(g.label)
            return (
              <div key={g.label} className="mt-3">
                <button
                  onClick={() => toggleGroup(g.label!)}
                  className="flex w-full items-center gap-1 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400 hover:text-gray-600"
                >
                  <span>{g.label}</span>
                  <ChevronDown className={cn('ml-auto h-3.5 w-3.5 transition-transform', !shown && '-rotate-90')} />
                </button>
                {shown && <div className="mt-0.5">{renderItems}</div>}
              </div>
            )
          })}
        </nav>

        {/* Footer: user + logout */}
        <div className="border-t border-gray-100 px-3 py-3">
          <div className="flex items-center gap-2 rounded-md px-3 py-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
              {user?.email?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-gray-900">{user?.email}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
            <button onClick={handleLogout} title="Sair" className="text-gray-400 hover:text-gray-600">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <VersaoNoAr />
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center border-b border-gray-200 bg-white px-4 lg:px-6">
          <button className="mr-3 lg:hidden" onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="font-display text-[15px] text-gray-900">
            {allItems.find((n) => n.to !== '/' && pathname.startsWith(n.to))?.label ??
              (pathname === '/' ? 'Dashboard' : '')}
          </h1>
          {isAdmin && activeClient && (
            <span className="ml-3 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
              {activeClient.name}
            </span>
          )}
          {isAdmin && !activeTenantId && (
            <span className="ml-3 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500">
              Todos os clientes
            </span>
          )}
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {blocked ? (
            <div className="mx-auto mt-16 max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center">
              <ShieldCheck className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-3 text-base font-semibold text-gray-900">Sem acesso a esta tela</p>
              <p className="mt-1 text-sm text-gray-500">Seu perfil não tem permissão para <b>{currentScreen?.label}</b>. Fale com o administrador se precisar deste acesso.</p>
              <button onClick={() => navigate('/')} className="mt-4 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Ir para o início</button>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  )
}

/** Qual versão está no ar — a da tela e a do servidor.
 *
 *  Existe porque "não vejo mudanças" e "a correção não funcionou" se parecem
 *  exatamente igual na tela, e sem SSH não havia como distinguir. Quando os dois
 *  commits diferem, o navegador está com o bundle antigo: um recarregamento
 *  resolve, e ninguém precisa investigar um defeito que já foi corrigido.
 */
function VersaoNoAr() {
  const [api, setApi] = useState<{ commit: string | null; deployedAt: string | null } | null>(null)
  useEffect(() => {
    let vivo = true
    fetch('/api/health')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivo && j) setApi({ commit: j.commit ?? null, deployedAt: j.deployedAt ?? null }) })
      .catch(() => { /* versão é conveniência: falhar aqui não pode quebrar a tela */ })
    return () => { vivo = false }
  }, [])

  const tela = __APP_COMMIT__
  const atrasada = !!api?.commit && api.commit !== tela
  const quando = api?.deployedAt ? new Date(api.deployedAt).toLocaleString('pt-BR') : null

  return (
    <div className="px-3 pb-1 pt-2">
      {atrasada ? (
        <button
          onClick={() => window.location.reload()}
          className="w-full rounded-md bg-amber-50 px-2 py-1.5 text-left text-[11px] leading-tight text-amber-800 hover:bg-amber-100"
          title={`Tela: ${tela} · Servidor: ${api?.commit}${quando ? ` (${quando})` : ''}`}>
          <b>Versão nova disponível.</b> Clique para recarregar.
        </button>
      ) : (
        <p className="truncate text-[11px] text-gray-300"
          title={`Tela: ${tela}${api?.commit ? ` · Servidor: ${api.commit}` : ''}${quando ? ` · no ar desde ${quando}` : ''}`}>
          versão {api?.commit ?? tela}{quando ? ` · ${quando}` : ''}
        </p>
      )}
    </div>
  )
}
